import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

/**
 * Export multiple sheets to a single .xlsx file.
 * @param {Array<{name: string, data: Array<Array>}>} sheets - Each sheet has a name and 2D array (first row = headers)
 * @param {string} filename - Output filename (without .xlsx extension)
 */
export function exportMultiSheet(sheets, filename) {
    const wb = XLSX.utils.book_new()

    sheets.forEach(sheet => {
        if (!sheet || !sheet.data || sheet.data.length === 0) return

        const ws = XLSX.utils.aoa_to_sheet(sheet.data)

        // Auto-size columns based on content width
        const colCount = sheet.data[0].length
        ws['!cols'] = Array.from({ length: colCount }, (_, i) => {
            const maxLen = sheet.data.reduce((max, row) => {
                const val = row[i]
                const len = val != null ? String(val).length : 0
                return Math.max(max, len)
            }, 0)
            return { wch: Math.min(Math.max(maxLen + 2, 10), 40) }
        })

        XLSX.utils.book_append_sheet(wb, ws, sheet.name.slice(0, 31))
    })

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`)
}

/**
 * Build sheet for Daily Production data
 */
export function buildDailyProductionSheet(production, isNet, prefix) {
    if (!production?.actual?.length) return null

    const forecastMap = {}
    if (production.forecast) {
        production.forecast.forEach(f => { forecastMap[f.date] = isNet ? f.net_oil_bbl : f.oil_bbl })
    }

    const headers = ['Date', `${prefix} Oil (BBL)`, `${prefix} Gas (MCF)`, 'Water (BBL)', `${prefix} Oil Forecast`]
    const rows = production.actual.map(d => [
        d.date,
        isNet ? (d.net_oil_bbl ?? '') : (d.oil_bbl ?? ''),
        isNet ? (d.net_gas_mcf ?? '') : (d.gas_mcf ?? ''),
        d.water_bbl ?? '',
        forecastMap[d.date] ?? ''
    ])

    return { name: 'Daily Production', data: [headers, ...rows] }
}

/**
 * Build sheet for WTI Price history
 */
export function buildWTISheet(wtiPrice) {
    if (!wtiPrice?.history?.length) return null

    const headers = ['Date', 'WTI Price ($/BBL)']
    const rows = wtiPrice.history.map(d => [
        d.date,
        d.price != null ? Math.round(d.price * 100) / 100 : ''
    ])

    return { name: 'WTI Price', data: [headers, ...rows] }
}

/**
 * Build sheet for Production Concentration (Pareto)
 */
export function buildConcentrationSheet(concentration) {
    if (!concentration?.length) return null

    // Calculate cumulative % if not already present
    const totalOil = concentration.reduce((sum, w) => sum + (w.oil || 0), 0)
    let cumOil = 0

    const headers = ['Well', 'Oil (BBL)', '% of Total', 'Cumulative %']
    const rows = concentration.map(w => {
        cumOil += (w.oil || 0)
        const pct = totalOil > 0 ? Math.round((w.oil / totalOil) * 10000) / 100 : 0
        const cumPct = totalOil > 0 ? Math.round((cumOil / totalOil) * 10000) / 100 : 0
        return [w.name || w.wellName || '', w.oil ?? '', pct, cumPct]
    })

    return { name: 'Production Concentration', data: [headers, ...rows] }
}

/**
 * Build sheet for Top Producing Wells
 */
export function buildTopWellsSheet(wells, isNet, prefix) {
    if (!wells?.length) return null

    const headers = ['Well', `${prefix} Oil (BBL/d)`, `${prefix} Gas (MCF/d)`, 'Water (BBL/d)', 'Uptime %']
    const rows = wells.map(w => [
        w.wellName || w.name || '',
        isNet ? (w.netOilPerDay ?? '') : (w.grossOilPerDay ?? w.oilPerDay ?? ''),
        isNet ? (w.netGasPerDay ?? '') : (w.grossGasPerDay ?? w.gasPerDay ?? ''),
        w.waterPerDay ?? '',
        w.uptimePercent != null ? Math.round(w.uptimePercent * 10) / 10 : ''
    ])

    return { name: 'Top Wells', data: [headers, ...rows] }
}

/**
 * Build sheet for Monthly Production data
 */
export function buildMonthlySheet(monthlyData, prefix, isNet) {
    if (!monthlyData?.monthly?.length) return null

    const oilActKey = isNet ? 'netOilActual' : 'grossOilActual'
    const oilFcstKey = isNet ? 'netOilForecast' : 'grossOilForecast'
    const varKey = isNet ? 'netOilVariancePct' : 'oilVariancePct'
    const gasActKey = isNet ? 'netGasActual' : 'grossGasActual'
    const gasFcstKey = isNet ? 'netGasForecast' : 'grossGasForecast'

    const headers = [
        'Month',
        `${prefix} Oil Actual (BBL)`,
        `${prefix} Oil Forecast (BBL)`,
        'Oil Variance %',
        `${prefix} Gas Actual (MCF)`,
        `${prefix} Gas Forecast (MCF)`
    ]

    const rows = monthlyData.monthly.map(m => [
        m.label,
        m[oilActKey] ?? '',
        m[oilFcstKey] ?? '',
        m[varKey] != null ? m[varKey] : '',
        m[gasActKey] ?? '',
        m[gasFcstKey] ?? ''
    ])

    return { name: 'Monthly Production', data: [headers, ...rows] }
}

// ============================================================
// ComboCurve Upload Builders
// ============================================================

/**
 * Build ComboCurve daily upload sheet.
 * Columns: INPT_ID | Chosen_ID | Well_Number | Date | Daily Oil | Daily Gas | Daily Water
 * Date format: M/D/YYYY (e.g. 1/15/2026) — already formatted by the API.
 */
export function buildCCDailySheet(rows) {
    if (!rows?.length) return null

    const headers = ['INPT_ID', 'Chosen_ID', 'Well_Number', 'Lease_Name', 'Date', 'Daily Oil', 'Daily Gas', 'Daily Water']
    const data = rows.map(r => [
        r.inpt_id,
        r.chosen_id,
        r.well_number,
        r.lease_name,
        r.prod_date,
        r.oil_bbl ?? 0,
        r.gas_mcf ?? 0,
        r.water_bbl ?? 0
    ])

    return { name: 'Daily Upload', data: [headers, ...data] }
}

/**
 * Build ComboCurve monthly upload sheet.
 * Columns: INPT_ID | Chosen_ID | Well_Number | Date | Monthly Oil | Monthly Gas | Monthly Water
 * Date format: M/15/YYYY — already formatted by the API.
 */
export function buildCCMonthlySheet(rows) {
    if (!rows?.length) return null

    const headers = ['INPT_ID', 'Chosen_ID', 'Well_Number', 'Lease_Name', 'Date', 'Monthly Oil', 'Monthly Gas', 'Monthly Water']
    const data = rows.map(r => [
        r.inpt_id,
        r.chosen_id,
        r.well_number,
        r.lease_name,
        r.prod_date,
        r.oil_bbl ?? 0,
        r.gas_mcf ?? 0,
        r.water_bbl ?? 0
    ])

    return { name: 'Monthly Upload', data: [headers, ...data] }
}

/**
 * Build ComboCurve quarterly sheets — returns an array of TWO sheet objects:
 *   [0] "Quarterly Upload" — same ComboCurve format, quarterly aggregated
 *   [1] "Investor Summary" — readable horizontal layout (one row per lease, quarters as columns)
 *
 * @param {Array} rows - rows from /api/downloads/data?format=quarterly
 * @returns {Array<{name, data}>} two sheet objects (filter nulls before use)
 */
export function buildCCQuarterlySheets(rows) {
    if (!rows?.length) return []

    // ---- Sheet 1: ComboCurve upload format ----
    const uploadHeaders = ['INPT_ID', 'Chosen_ID', 'Well_Number', 'Lease_Name', 'Date', 'Quarterly Oil', 'Quarterly Gas', 'Quarterly Water']
    const uploadData = rows.map(r => [
        r.inpt_id,
        r.chosen_id,
        r.well_number,
        r.lease_name,
        r.prod_date,
        r.oil_bbl ?? 0,
        r.gas_mcf ?? 0,
        r.water_bbl ?? 0
    ])
    const uploadSheet = { name: 'Quarterly Upload', data: [uploadHeaders, ...uploadData] }

    // ---- Sheet 2: Investor summary (horizontal quarters) ----
    // Derive unique quarters from prod_dates (format M/15/YYYY → Q label)
    const quarterLabels = [...new Set(rows.map(r => prodDateToQLabel(r.prod_date)))].sort()

    // Build per-lease row map
    const leaseMap = {}
    for (const r of rows) {
        if (!leaseMap[r.inpt_id]) {
            leaseMap[r.inpt_id] = { lease_name: r.lease_name, quarters: {} }
        }
        const q = prodDateToQLabel(r.prod_date)
        leaseMap[r.inpt_id].quarters[q] = {
            oil: r.oil_bbl ?? 0,
            gas: r.gas_mcf ?? 0,
            water: r.water_bbl ?? 0
        }
    }

    // Build header row: Lease Name | Q1 YYYY Oil | Q1 YYYY Gas | Q1 YYYY Water | Q2 ...
    const summaryHeaders = ['Lease Name']
    for (const q of quarterLabels) {
        summaryHeaders.push(`${q} Oil (BBL)`, `${q} Gas (MCF)`, `${q} Water (BBL)`)
    }

    const summaryData = Object.values(leaseMap)
        .sort((a, b) => a.lease_name.localeCompare(b.lease_name))
        .map(l => {
            const row = [l.lease_name]
            for (const q of quarterLabels) {
                const qd = l.quarters[q] || { oil: 0, gas: 0, water: 0 }
                row.push(qd.oil, qd.gas, qd.water)
            }
            return row
        })

    const summarySheet = { name: 'Investor Summary', data: [summaryHeaders, ...summaryData] }

    return [uploadSheet, summarySheet]
}

/**
 * Convert a prod_date string (M/15/YYYY or M/1/YYYY) to a quarter label like "Q1 2025".
 * Month 1-3 → Q1, 4-6 → Q2, 7-9 → Q3, 10-12 → Q4.
 */
function prodDateToQLabel(prodDate) {
    const parts = prodDate.split('/')
    const month = parseInt(parts[0], 10)
    const year = parts[2]
    const q = Math.ceil(month / 3)
    return `Q${q} ${year}`
}

// ============================================================

/**
 * Build sheet for Quarterly Production data
 */
export function buildQuarterlySheet(monthlyData, prefix, isNet) {
    if (!monthlyData?.quarterly?.length) return null

    const oilActKey = isNet ? 'netOilActual' : 'grossOilActual'
    const oilFcstKey = isNet ? 'netOilForecast' : 'grossOilForecast'
    const varKey = isNet ? 'netOilVariancePct' : 'oilVariancePct'

    const headers = ['Quarter', `${prefix} Oil Actual (BBL)`, `${prefix} Oil Forecast (BBL)`, 'Variance %']
    const rows = monthlyData.quarterly.map(q => [
        q.label,
        q[oilActKey] ?? '',
        q[oilFcstKey] ?? '',
        q[varKey] != null ? q[varKey] : ''
    ])

    return { name: 'Quarterly Summary', data: [headers, ...rows] }
}
